/**
 * RTK Query API Slice for External API Proxy Operations
 * 
 * Handles all external API proxy endpoints including:
 * - Python API recording processing
 * - Bulk user import operations
 * - External service integration
 * - File upload and processing
 * - Progress tracking for long-running operations
 * 
 * Key architectural decisions:
 * - Uses existing apiClient as baseQuery (maintains snake_case)
 * - All external API calls go through backend proxy for security
 * - API keys are injected server-side (never exposed to frontend)
 * - Progress tracking for large file operations
 * - Error handling for external service failures
 * - Support for streaming and binary data
 * - Timeout handling for long-running operations
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import apiClientBaseQuery from './baseQuery';

export const externalApi = createApi({
  reducerPath: 'externalApi',
  baseQuery: apiClientBaseQuery,
  
  // External operations should not be cached due to processing nature
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: false, // Don't automatically refetch processing results
  refetchOnFocus: false,
  
  // Define cache tags for invalidation
  tagTypes: ['ProcessingJob', 'ImportJob', 'ExternalStatus'],
  
  endpoints: (builder) => ({
    // Process recording via Python API
    processRecording: builder.mutation({
      query: (recordingData) => ({
        url: '/v1/process-recording',
        method: 'POST',
        data: {
          // Recording file or data
          recording_file: recordingData.recording_file,
          recording_data: recordingData.recording_data,
          
          // Processing options
          processing_type: recordingData.processing_type || 'standard',
          analysis_level: recordingData.analysis_level || 'basic', // basic, detailed, comprehensive
          include_raw_metrics: recordingData.include_raw_metrics || false,
          
          // Patient/User context
          user_id: recordingData.user_id,
          patient_age: recordingData.patient_age,
          patient_gender: recordingData.patient_gender,
          patient_weight: recordingData.patient_weight,
          patient_height: recordingData.patient_height,
          
          // Recording metadata
          recording_duration: recordingData.recording_duration,
          sample_rate: recordingData.sample_rate,
          recording_date: recordingData.recording_date,
          device_type: recordingData.device_type,
          
          // Processing preferences
          output_format: recordingData.output_format || 'json',
          include_visualization: recordingData.include_visualization || false,
          custom_parameters: recordingData.custom_parameters || {},
          
          // Callback configuration for progress tracking
          callback_url: recordingData.callback_url,
          webhook_secret: recordingData.webhook_secret,
        },
      }),
      transformResponse: (response) => {
        // Python API processing returns job ID and initial results
        return response.data || {};
      },
      // Processing results should not be cached
      keepUnusedDataFor: 0,
    }),

    // Get processing job status
    getProcessingStatus: builder.query({
      query: (jobId) => ({
        url: `/process-recording/status/${jobId}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        // Status includes: pending, processing, completed, failed, cancelled
        return response.data || { status: 'unknown' };
      },
      // Status should be checked in real-time
      keepUnusedDataFor: 0,
      providesTags: (result, error, jobId) => [
        { type: 'ProcessingJob', id: jobId }
      ],
    }),

    // Cancel processing job
    cancelProcessing: builder.mutation({
      query: (jobId) => ({
        url: `/process-recording/cancel/${jobId}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, jobId) => [
        { type: 'ProcessingJob', id: jobId }
      ],
    }),

    // Bulk user import - Node.js proxies to Python API
    // Frontend -> /v1/bulk_user_import (Node) -> /api/bulk_user_import (Python)
    bulkUserImport: builder.mutation({
      query: (formData) => ({
        url: '/v1/bulk_user_import',
        method: 'POST',
        data: formData,
        headers: {
          // Let the browser set the Content-Type with boundary for FormData
          'Content-Type': undefined,
        },
      }),
      transformResponse: (response) => {
        // Handle the response from the backend
        return response || {};
      },
      keepUnusedDataFor: 0,
    }),

    // Get bulk import job status
    getImportStatus: builder.query({
      query: (jobId) => ({
        url: `/bulk-user-import/status/${jobId}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        // Status includes progress, errors, success count, etc.
        return response.data || { 
          status: 'unknown', 
          progress: 0, 
          total_records: 0, 
          processed_records: 0,
          successful_imports: 0,
          failed_imports: 0,
          errors: []
        };
      },
      keepUnusedDataFor: 0,
      providesTags: (result, error, jobId) => [
        { type: 'ImportJob', id: jobId }
      ],
    }),

    // Cancel bulk import job
    cancelImport: builder.mutation({
      query: (jobId) => ({
        url: `/bulk-user-import/cancel/${jobId}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, jobId) => [
        { type: 'ImportJob', id: jobId }
      ],
    }),

    // Get import job results
    getImportResults: builder.query({
      query: (jobId) => ({
        url: `/bulk-user-import/results/${jobId}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        // Results include successful imports, errors, rejected records
        return response.data || {
          successful_imports: [],
          failed_imports: [],
          validation_errors: [],
          summary: {}
        };
      },
      keepUnusedDataFor: 300, // Cache results for 5 minutes
    }),

    // Validate import data before processing
    validateImportData: builder.mutation({
      query: (validationData) => ({
        url: '/v1/bulk-user-import/validate',
        method: 'POST',
        data: {
          import_file: validationData.import_file,
          import_data: validationData.import_data,
          file_format: validationData.file_format || 'csv',
          field_mapping: validationData.field_mapping || {},
          required_fields: validationData.required_fields || [],
          client_id: validationData.client_id,
          check_duplicates: validationData.check_duplicates !== false,
          validate_emails: validationData.validate_emails !== false,
        },
      }),
      transformResponse: (response) => {
        // Validation returns field analysis, errors, warnings, preview data
        return response.data || {
          is_valid: false,
          errors: [],
          warnings: [],
          field_analysis: {},
          preview_data: [],
          total_records: 0
        };
      },
    }),

    // Get external API health status
    getExternalApiStatus: builder.query({
      query: () => ({
        url: '/v1/external-status',
        method: 'GET',
      }),
      transformResponse: (response) => {
        // External API status includes Python API health, response times
        return response.data || { 
          python_api: { status: 'unknown', response_time: null },
          last_check: null 
        };
      },
      // Check status frequently but allow brief caching
      keepUnusedDataFor: 30, // 30 seconds
      providesTags: ['ExternalStatus'],
    }),

    // Test external API connectivity
    testExternalConnection: builder.mutation({
      query: (testData = {}) => ({
        url: '/v1/test-external-connection',
        method: 'POST',
        data: {
          endpoint: testData.endpoint || 'health',
          timeout: testData.timeout || 10,
          include_auth: testData.include_auth !== false,
        },
      }),
      transformResponse: (response) => {
        return response.data || { success: false, response_time: null };
      },
    }),

    // Get processing job logs
    getProcessingLogs: builder.query({
      query: (params) => ({
        url: `/process-recording/logs/${params.jobId}`,
        method: 'GET',
        params: {
          level: params.level || 'info', // debug, info, warn, error
          limit: params.limit || 100,
          offset: params.offset || 0,
        },
      }),
      transformResponse: (response) => {
        return response.data || { logs: [], total: 0 };
      },
      keepUnusedDataFor: 0,
    }),

    // Retry failed processing job
    retryProcessing: builder.mutation({
      query: (retryData) => ({
        url: `/process-recording/retry/${retryData.jobId}`,
        method: 'POST',
        data: {
          retry_options: retryData.retry_options || {},
          reset_progress: retryData.reset_progress || false,
        },
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'ProcessingJob', id: jobId }
      ],
    }),

    // Get processing queue status
    getProcessingQueue: builder.query({
      query: (params = {}) => ({
        url: '/v1/process-recording/queue',
        method: 'GET',
        params: {
          status: params.status, // pending, processing, completed, failed
          user_id: params.user_id,
          client_id: params.client_id,
          limit: params.limit || 50,
          offset: params.offset || 0,
        },
      }),
      transformResponse: (response) => {
        return response.data || { jobs: [], total: 0, queue_stats: {} };
      },
      keepUnusedDataFor: 30, // 30 seconds
    }),

    // Download processed results
    downloadProcessingResults: builder.mutation({
      query: (downloadData) => ({
        url: `/process-recording/download/${downloadData.jobId}`,
        method: 'GET',
        params: {
          format: downloadData.format || 'json', // json, csv, pdf
          include_raw: downloadData.include_raw || false,
        },
      }),
      transformResponse: (response, meta, arg) => {
        // Handle binary data download
        if (response instanceof Blob) {
          const url = window.URL.createObjectURL(response);
          return { 
            url, 
            filename: `processing_results_${arg.jobId}.${arg.format || 'json'}`,
            blob: response 
          };
        }
        return response.data || {};
      },
    }),

    // Get external API usage statistics
    getExternalApiUsage: builder.query({
      query: (params = {}) => ({
        url: '/v1/external-api-usage',
        method: 'GET',
        params: {
          period: params.period || '24h',
          endpoint: params.endpoint,
          client_id: params.client_id,
          include_costs: params.include_costs || false,
        },
      }),
      transformResponse: (response) => {
        return response.data || { usage: [], summary: {}, costs: {} };
      },
      keepUnusedDataFor: 300, // 5 minutes
    }),

    // Configure external API settings (admin only)
    configureExternalApi: builder.mutation({
      query: (configData) => ({
        url: '/v1/configure-external-api',
        method: 'PUT',
        data: {
          api_endpoint: configData.api_endpoint,
          timeout: configData.timeout || 30,
          retry_attempts: configData.retry_attempts || 3,
          rate_limit: configData.rate_limit,
          enable_webhooks: configData.enable_webhooks || false,
          webhook_secret: configData.webhook_secret,
          // API key is handled server-side only
        },
      }),
      invalidatesTags: ['ExternalStatus'],
    }),
  }),
});

// Export hooks for use in components
export const {
  // Queries
  useGetProcessingStatusQuery,
  useGetImportStatusQuery,
  useGetImportResultsQuery,
  useGetExternalApiStatusQuery,
  useGetProcessingLogsQuery,
  useGetProcessingQueueQuery,
  useGetExternalApiUsageQuery,
  
  // Mutations
  useProcessRecordingMutation,
  useBulkUserImportMutation,
  useCancelProcessingMutation,
  useCancelImportMutation,
  useValidateImportDataMutation,
  useTestExternalConnectionMutation,
  useRetryProcessingMutation,
  useDownloadProcessingResultsMutation,
  useConfigureExternalApiMutation,
  
  // Lazy queries (for manual triggering)
  useLazyGetProcessingStatusQuery,
  useLazyGetImportStatusQuery,
  useLazyGetProcessingQueueQuery,
  
  // For manual cache invalidation if needed
  util: { invalidateTags },
} = externalApi;

export default externalApi;